import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react'
import { FilterIcon, StoriesIcon, NotebookIcon, GridIcon, ListIcon } from '@/components/Icons'
import Head from 'next/head'
import { GetStaticProps } from 'next'
import GetAllCategories from '@/services/api/products/GetAllCategories'
import { GetAllAttributes } from '@/services/api/products/GetAttributeOption'
import { useRouter } from 'next/router'
import ProductItemList from '@/components/Products/ProductItemList'
import { AnimatePresence, motion } from 'framer-motion'
import Loader from '@/components/Loader'
import useProductQuery from '@/hooks/useProductQuery'
import NewProductItem from '@/components/Products/NewProductItem'
import Image from 'next/image'
import HeroBanner from '@/components/Products/HeroBanner'
import CategoryTabs from '@/components/Products/CategoryTabs'
import FiltersSidebar from '@/components/Products/FiltersSidebar'
import useResponsiveColumns from '@/hooks/useResponsiveColumns'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import useUserPreferences from '@/hooks/useUserPreferences'
import dynamic from 'next/dynamic'

// Lazy load modal components (they are not needed initially)
const LazyFilterModal = dynamic(() => import('@/components/Auth/FilterModal'), { ssr: false })
const LazyChildDataModal = dynamic(() => import('@/components/Products/ChildDataModal'), { ssr: false })

export const getStaticProps: GetStaticProps = async ({ locale }) => {
    const [categories_list, attributes] = await Promise.all([
      GetAllCategories(),
      GetAllAttributes(),
    ])

    return {
      props: {
        ...(await serverSideTranslations(locale ?? 'en', [
          'products',
          'productsHeroBanner',
          'categoryTabs',
          'filterModal',
          'filterSidebar',
          'desktopHeader',
          'footer',
          'common',
          'account',
          'alerts',
          'address',
          'cart',
          'contactus',
          'deliveryPolicy',
          'notfound',
          'payment',
          'personalizeModal',
          'product',
          'removePhone'
        ])),
        categories_list,
        attributes,
      },
    }
}

interface Props {
  categories_list: any
  attributes: any
}

// Memoized product item components
const ProductList = React.memo(({ products, gender, name }: any) => (
  <div className='product-list lg:grid-cols-3 lg:gap-4 mt-4' id='products-cards-section'>
    {products.map((product: any, index: number) => (
      <div className="prd-item md:mt-0" key={`${product.id}-${index}`}>
        <ProductItemList product={product} gender={gender} name={name == '' ? undefined : name} />
      </div>
    ))}
  </div>
));
ProductList.displayName = 'ProductList';

const ProductGrid = React.memo(({ productsWithAd, gender, name, locale }: any) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 lg:gap-4 mt-4 px-4">
    {productsWithAd.map((item: any, index: number) => {
      if (item.type === 'ad') {
        return (
          <div className="prd-item mt-2 md:mt-0" key={`ad-${index}`}>
            <div className="bg-white text-center min-w-[45vw] h-[300px] md:min-w-[30vw] md:h-[380px] lg:min-w-[18vw] lg:h-[450px] rounded-2xl object-contain object-top">
              <Image
                src={'/images/banner-mob-old.webp'}
                width={300}
                height={250}
                alt="ad"
                loading="lazy"
                className="w-full rounded-2xl h-full md:max-h-[380px] lg:max-h-[430px] object-cover object-top"
              />
            </div>
          </div>
        )
      }
      return (
        <div className="prd-item mt-2 md:mt-0" key={`${item.id}-${index}`}>
          <NewProductItem
            product={item}
            gender={gender}
            name={name || (locale === 'ar' ? item?.replace_name?.ar : item?.replace_name?.en)}
          />
        </div>
      )
    })}
  </div>
));
ProductGrid.displayName = 'ProductGrid';

export default function Products({ categories_list, attributes }: Props) {
  const { t } = useTranslation('products')
  const router = useRouter()
  const { locale } = router
  const preferences = useUserPreferences();
  const country = preferences?.countryCode;

  // states
  const [isOpen, setIsOpen] = useState(false)
  const [layout, setLayout] = useState<'list' | 'grid'>('grid')
  const [childDataModal, setChildDataModal] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [allProducts, setAllProducts] = useState<any[]>([])
  const [showScrollTop, setShowScrollTop] = useState(false)
  const observerRef = useRef<HTMLDivElement | null>(null)

  // product query hook
  const {
    params: {
      name,
      page,
      categories,
      options,
      gender,
      query: searchQuery,
      sort,
      minPrice,
      maxPrice,
      product_type,
      is_ai
    },
    data,
    isLoading,
    isRefetching,
    setPage,
    setSort,
    setCount,
    setMinPrice,
    setMaxPrice,
    toggleCategory,
    toggleOption,
    setProductMode
  } = useProductQuery()

  const columns = useResponsiveColumns()
  const currentCategory = categories[0] || ''
  const currentProductType = product_type

  // Save/Load state (only once on mount)
  useEffect(() => {
    const loadProductsState = () => {
      const products = sessionStorage.getItem('products')
      const scrollPos = sessionStorage.getItem('scrollPos')
      if (products) {
        setAllProducts(JSON.parse(products))
        window.scrollTo(0, parseInt(scrollPos || '0'))
      }
    }
    loadProductsState()
  }, [])

  // Save state on route change
  useEffect(() => {
    const handleRouteChange = () => {
      sessionStorage.setItem('products', JSON.stringify(allProducts))
      sessionStorage.setItem('scrollPos', window.scrollY.toString())
    }
    router.events.on('routeChangeStart', handleRouteChange)
    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [allProducts, router.events])

  // Scroll top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > window.innerHeight * 2)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Set loaded after mount
  useEffect(() => setLoaded(true), [])

  // Update products when data changes
  useEffect(() => {
    if (!data?.data) return;
    if (page === 1) {
      setAllProducts(data.data);
    } else {
      setAllProducts(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const newProducts = data.data.filter((p: any) => !existingIds.has(p.id));
        return [...prev, ...newProducts];
      });
    }
  }, [data?.data, page])

  // Memoize filtered products
  const filteredProducts = useMemo(() => {
    return allProducts.filter((product: any) => {
      const productTypeMatch = product.product_type?.toString() === product_type
      const genderMatch = gender === '' || 
        (gender === 'Female' && product.has_female) ||
        (gender === 'Male' && product.has_male)
      const aiMatch = product_type === '1'
        ? is_ai === '1' ? product.is_ai === 1 : is_ai === '0' ? product.is_ai !== 1 : true
        : true
      return productTypeMatch && genderMatch && aiMatch
    })
  }, [allProducts, product_type, gender, is_ai])

  // Memoize products with ad
  const productsWithAd = useMemo(() => {
    if (layout !== 'grid' || filteredProducts.length <= columns) return filteredProducts
    const fullRows = Math.floor(filteredProducts.length / columns)
    if (fullRows < 2) return filteredProducts
    const possibleInsertIndices = Array.from(
      { length: fullRows },
      (_, i) => (i + 1) * columns
    ).filter(index => index < filteredProducts.length)
    if (possibleInsertIndices.length === 0) return filteredProducts
    const randomAdIndex = possibleInsertIndices[Math.floor(Math.random() * possibleInsertIndices.length)]
    return [
      ...filteredProducts.slice(0, randomAdIndex),
      { type: 'ad', id: `ad-${Date.now()}` },
      ...filteredProducts.slice(randomAdIndex),
    ]
  }, [filteredProducts, layout, columns])

  // Memoize boolean states
  const shouldStopInfiniteScroll = useMemo(() => {
    return isLoading || !data?.last_page || page >= data.last_page;
  }, [isLoading, page, data?.last_page]);

  const hasVisibleProducts = useMemo(() => filteredProducts.length > 0, [filteredProducts]);
  const isEmptyFirstLoad = useMemo(() => {
    return !isLoading && page === 1 && allProducts.length === 0;
  }, [isLoading, page, allProducts.length]);

  // Infinite scroll observer (useCallback for stable dependency)
  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (entry.isIntersecting && !shouldStopInfiniteScroll && !isLoading) {
      setPage(page + 1);
    }
  }, [shouldStopInfiniteScroll, isLoading, page, setPage]);

  useEffect(() => {
    const targetNode = observerRef.current;
    if (shouldStopInfiniteScroll || !targetNode) return;
    const observer = new IntersectionObserver(handleIntersection, { threshold: 0.5, rootMargin: '100px' });
    observer.observe(targetNode);
    return () => observer.unobserve(targetNode);
  }, [handleIntersection, shouldStopInfiniteScroll]);

  // Handle product mode change
  const handleProductModeChange = useCallback((type: string, ai?: string) => {
    setAllProducts([]);
    setPage(1);
    setProductMode(type, ai);
  }, [setPage, setProductMode]);

  // Handler for filter reset
  const resetFilters = useCallback(() => {
    router.push('/products');
  }, [router]);

  // For debugging (remove in production)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('Products Debug:', {
        page, product_type, is_ai,
        allProductsCount: allProducts.length,
        filteredProductsCount: filteredProducts.length,
        lastPage: data?.last_page,
        isLoading,
      });
    }
  }, [page, product_type, is_ai, allProducts, filteredProducts, data?.last_page, isLoading]);

  if (!router.isReady) return null;

  return (
    <>
      <Head>
        <title>Basmti</title>
        <meta name="description" content="Basmti" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      {loaded && (
        <main className="bg-white pb-24 lg:pt-4">
          <HeroBanner
            onCustomizeClick={() => setChildDataModal(true)}
            name={name}
          />

          <div
            className={`lg:container-fluid lg:grid category-container lg:mt-10 lg:grid-cols-[300px_1fr]`}
            id='products-container'
            dir={locale === 'en' ? 'ltr' : 'rtl'}
          >
            <div>
              <FiltersSidebar
                categoriesList={categories_list}
                attributes={attributes}
                filtersCategories={categories.join(',')}
                options={options.join(',')}
                CheckExistsCategory={cat => categories.includes(cat)}
                UpdateCategory={toggleCategory}
                CheckExistsOption={opt => options.includes(opt)}
                UpdateOptions={toggleOption}
                UpdateMinPrice={setMinPrice}
                UpdateMaxPrice={setMaxPrice}
                productType={currentProductType}
                is_ai={is_ai}
                UpdateAIFilter={(aiFlag) => handleProductModeChange('1', aiFlag)}
              />
            </div>

            <div className={`product-list-container relative ${locale === 'en' ? 'order-2' : 'order-1'}`}>
              <div className='sticky lg:static top-[64px] md:top-0 z-50 bg-white w-full pt-4 pb-2 px-4 shadow-xl lg:shadow-none'>
                <div className="flex flex-col items-center justify-center gap-2 w-full">
                  <div className='main-filter flex items-center w-full relative'>
                    <div className="flex items-center gap-2 sm:gap-4 flex-nowrap overflow-x-auto scrollbar-hide">
                      <button
                        onClick={() => handleProductModeChange('1')}
                        id="personal-stories"
                        className={`${product_type == '1' ? 'bg-primary text-white p-2 sm:p-4 rounded-lg' : 'text-[#00214D66]'} text-xs sm:text-base lg:text-base px-4 sm:px-6 lg:px-10 flex items-center justify-center gap-2 font-medium whitespace-nowrap flex-shrink-0`}
                      >
                        <span className="flex-shrink-0"><StoriesIcon /></span>
                        <span className="whitespace-nowrap">{t('personalStories')}</span>
                      </button>

                      {country === 'IL' && (
                        <button
                          onClick={() => handleProductModeChange('2')}
                          id="personal-notebooks"
                          className={`${product_type == '2' ? 'bg-primary text-white p-2 sm:p-4 rounded-lg' : 'text-[#00214D66]'} text-xs sm:text-base lg:text-base px-4 sm:px-6 lg:px-10 flex items-center justify-center gap-2 font-medium whitespace-nowrap flex-shrink-0`}
                        >
                          <span className="flex-shrink-0"><NotebookIcon /></span>
                          <span className="whitespace-nowrap">{t('personalNotebooks')}</span>
                        </button>
                      )}
                    </div>

                    <div
                      className={`absolute p-2 rounded-lg border-[1px] border-primary w-10 h-10 flex items-center justify-center ${locale === 'ar' ? 'left-0' : 'right-0'} lg:hidden bg-white`}
                      onClick={() => setIsOpen(true)}
                    >
                      <FilterIcon />
                    </div>
                  </div>
                </div>

                <CategoryTabs
                  categories={categories_list}
                  currentCategory={currentCategory}
                  productType={currentProductType}
                  childName={name}
                  productsQuery={searchQuery}
                  childGender={gender}
                  is_ai={is_ai}
                />
              </div>

              <div className="desktop-filters items-center justify-between hidden lg:flex mt-6">
                <div className="filter flex items-center">
                  <button onClick={() => setLayout('grid')} className={`cursor-pointer mr-4 ${layout == 'grid' ? '' : 'opacity-30'}`}>
                    <GridIcon />
                  </button>
                  <button onClick={() => setLayout('list')} className={`cursor-pointer mr-4 ${layout === 'list' ? '' : 'opacity-30'}`}>
                    <ListIcon />
                  </button>
                </div>
                <div className="left-filters flex items-center">
                  <select
                    className="ml-6 bg-transparent border-none"
                    value={sort}
                    onChange={e => {
                      setAllProducts([]);
                      setPage(1);
                      setSort(e.target.value);
                    }}
                  >
                    <option value="">{t('defaultSort')}</option>
                    <option value="high-to-low">{t('highToLow')}</option>
                    <option value="low-to-high">{t('lowToHigh')}</option>
                    <option value="most-popular">{t('mostPopular')}</option>
                  </select>
                </div>
              </div>

              {isRefetching && (
                <div className="text-center py-4">
                  <div className="inline-flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <span>{t('Applying changes')}</span>
                  </div>
                </div>
              )}

              {isLoading && page === 1 && <Loader />}

              {isEmptyFirstLoad ? (
                <div className="text-center py-12 px-4">
                  <div className="max-w-md mx-auto">
                    <div className="text-4xl mb-4">😔</div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-3">
                      {t('noProductsForGenderTitle', 'No Products Available')}
                    </h2>
                    <p className="text-gray-600 mb-6">
                      {t('noProductsForGenderDescription', 'No products found matching your current filters. Try adjusting your selections.')}
                    </p>
                    <button
                      onClick={resetFilters}
                      className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition-colors"
                    >
                      {t('resetFilters', 'Reset Filters')}
                    </button>
                  </div>
                </div>
              ) : (
                hasVisibleProducts ? (
                  <>
                    {layout == 'list' ? (
                      <AnimatePresence mode="wait">
                        <motion.div
                          key="list"
                          initial={{ opacity: 0, translateY: '60%' }}
                          transition={{ type: 'spring', duration: 0.5 }}
                          animate={{ opacity: 1, translateY: '0' }}
                          exit={{ opacity: 0, translateY: '60%' }}
                        >
                          <ProductList products={filteredProducts} gender={gender} name={name} />
                        </motion.div>
                      </AnimatePresence>
                    ) : (
                      <AnimatePresence mode="wait">
                        <motion.div
                          key="grid"
                          initial={{ opacity: 0, translateY: '60%' }}
                          transition={{ type: 'spring', duration: 0.5 }}
                          animate={{ opacity: 1, translateY: '0' }}
                          exit={{ opacity: 0, translateY: '60%' }}
                        >
                          <ProductGrid productsWithAd={productsWithAd} gender={gender} name={name} locale={locale} />
                        </motion.div>
                      </AnimatePresence>
                    )}
                  </>
                ) : (
                  !isLoading && (
                    <div className="text-center py-12 px-4">
                      <h1 className="text-xl font-semibold text-gray-800 mb-3">
                        {t('noProductsForGender', 'No products available for the selected gender')}
                      </h1>
                      <p className="text-gray-600">
                        {t('tryDifferentGender', 'Try selecting a different gender or browse all products')}
                      </p>
                    </div>
                  )
                )
              )}

              {/* Infinite scroll trigger */}
              {!shouldStopInfiniteScroll && (
                <div ref={observerRef} className="h-10 w-full flex justify-center items-center">
                  {isLoading && page > 1 && <Loader />}
                </div>
              )}
            </div>

            {/* Lazy loaded modals */}
            {childDataModal && (
              <LazyChildDataModal
                isOpen={childDataModal}
                onClose={() => setChildDataModal(false)}
                productType={currentProductType || '1'}
              />
            )}

            {showScrollTop && (
              <button
                className="fixed lg:hidden shadow-2xl right-10 bottom-10 w-10 h-10 bg-[#0CDFBF] text-primary flex items-center justify-center rounded-full"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                aria-label="Scroll to top"
              >
                <svg
                  fill="currentColor"
                  width="28px"
                  height="28px"
                  viewBox="-8.5 0 32 32"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M0.84 20.040c-0.2 0-0.44 -0.080-0.6 -0.24-0.32-0.32-0.32-0.84 0-1.2l6.44-6.44c0.32-0.32 0.88-0.32 1.2 0l6.44 6.44c0.32 0.32 0.32 0.84 0 1.2-0.32 0.32-0.84 0.32-1.2 0l-5.84-5.84-5.84 5.84c-0.16 0.16-0.4 0.24-0.6 0.24z"></path>
                </svg>
              </button>
            )}
          </div>
        </main>
      )}
      {isOpen && (
        <LazyFilterModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          categories_list={categories_list}
          attributes={attributes}
          layout={layout}
          setLayout={setLayout}
          UpdateCount={setCount}
          UpdateSort={setSort}
        />
      )}
    </>
  )
}